import axios, {AxiosError} from 'axios'
import {getFilePath, viewerRequestUsesCredentials} from '../../helpers/file-helpers'
import {resolveRoleIndex} from '../../roles'
import {
  documentArchiveUrl,
  documentByIdUrl,
  documentDeleteByIdUrl,
  documentDownloadUrl,
  documentsDeleteUrl,
  documentHistoryUrl,
  documentPrintUrl,
  documentTemplatesDeleteUrl,
  documentTemplatesUpdateUrl,
  documentTemplatesUrl,
  documentVerifyUrl,
  documentsGenerateUrl,
  documentsUrl,
} from '../../redux/api'
import type {
  ApiMessage,
  DocumentHistoryEntry,
  DocumentLanguage,
  DocumentTemplate,
  DocumentType,
  DocumentVerifyResult,
  DocumentsListMeta,
  GeneratedDocument,
} from './types'

export type CreateDocumentTemplatePayload = {
  name: string
  type: DocumentType
  establishment: string
  htmlTemplate: string
  slug?: string
  language?: DocumentLanguage
  active?: boolean
  requiresQRCode?: boolean
  requiresSignature?: boolean
}

export function parseApiMessage(err: unknown, locale: 'fr' | 'en' | 'ar' = 'fr'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as {message?: ApiMessage; en?: string; fr?: string} | undefined
    if (data?.message) {
      return data.message[locale] || data.message.en || data.message.fr || err.message
    }
    if (typeof data?.fr === 'string') return data.fr
    if (typeof data?.en === 'string') return data.en
  }
  return err instanceof Error ? err.message : 'Request failed'
}

/** Human-readable hint when the documents API returns 403. */
const ACCESS_ERROR_403: Record<'fr' | 'en' | 'ar', string> = {
  en: 'Check that the documents module is deployed and your account has staff access (admin, finance manager, or secretary — roles 2, 6, 10). Staff should be allowed on any establishment (same as payments), not only schools linked on the user profile.',
  fr: "Vérifiez que le module documents est déployé et que votre compte a les droits staff (admin, gestionnaire financier ou secrétariat — rôles 2, 6, 10). L'accès doit couvrir tous les établissements (comme les paiements), pas seulement ceux liés au profil utilisateur.",
  ar: 'تحقق من نشر وحدة الوثائق ومن أن حسابك يملك صلاحيات الموظفين (مدير، مالية أو سكرتارية — الأدوار 2، 6، 10). يجب أن يشمل الوصول كل المؤسسات (كما في المدفوعات).',
}

export function parseDocumentsAccessError(
  err: unknown,
  role: string | undefined,
  locale: 'fr' | 'en' | 'ar' = 'fr'
): string {
  const apiMsg = parseApiMessage(err, locale)
  if (!isAxiosForbidden(err)) {
    return apiMsg
  }

  void role
  return `${apiMsg} — ${ACCESS_ERROR_403[locale] ?? ACCESS_ERROR_403.fr}`
}

export async function createDocumentTemplate(
  payload: CreateDocumentTemplatePayload
): Promise<DocumentTemplate> {
  const {data} = await axios.post(documentTemplatesUrl, payload)
  const template = (data as {template?: DocumentTemplate}).template
  if (!template) throw new Error('Invalid create template response')
  return template
}

export async function updateDocumentTemplate(
  id: string,
  payload: Partial<CreateDocumentTemplatePayload>
): Promise<DocumentTemplate> {
  const {data} = await axios.post(documentTemplatesUpdateUrl, {id, ...payload})
  const template = (data as {template?: DocumentTemplate}).template
  if (!template) throw new Error('Invalid update template response')
  return template
}

export async function deleteDocumentTemplate(id: string): Promise<DocumentTemplate> {
  const {data} = await axios.post(documentTemplatesDeleteUrl, {id})
  const template = (data as {template?: DocumentTemplate}).template
  if (!template) throw new Error('Invalid delete template response')
  return template
}

export async function fetchDocumentTemplates(params: {
  establishment?: string
  type?: string
  active?: boolean
}): Promise<DocumentTemplate[]> {
  const {data} = await axios.get(documentTemplatesUrl, {params})
  const templates = (data as {templates?: DocumentTemplate[]}).templates
  return Array.isArray(templates) ? templates : []
}

export async function fetchDocuments(params: {
  page?: number
  limit?: number
  student?: string
  type?: string
  status?: string
  schoolYear?: string
}): Promise<{documents: GeneratedDocument[]; meta: DocumentsListMeta | null}> {
  const {data} = await axios.get(documentsUrl, {params})
  const payload = data as {
    documents?: GeneratedDocument[]
    meta?: DocumentsListMeta
  }
  return {
    documents: Array.isArray(payload.documents) ? payload.documents : [],
    meta: payload.meta ?? null,
  }
}

const PDF_ACCEPT = 'application/pdf,application/octet-stream,*/*'

export type DocumentDownloadResult = {
  fileName: string
  /** True when server returned HTML fallback instead of PDF. */
  isHtmlFallback: boolean
}

function getAuthToken(): string {
  const tokenKey = process.env.REACT_APP_AUTH_TOKEN_KEY || 'token'
  try {
    return localStorage.getItem(tokenKey)?.trim() || ''
  } catch {
    return ''
  }
}

function pickApiMessage(payload: {
  message?: ApiMessage | string
  fr?: string
  en?: string
  errorCode?: string
}): string {
  if (typeof payload.message === 'string') return payload.message
  if (payload.message?.fr) return payload.message.fr
  if (payload.message?.en) return payload.message.en
  if (payload.fr) return payload.fr
  if (payload.en) return payload.en
  if (payload.errorCode === 'SNAPSHOT_MISSING') {
    return 'Document file unavailable (snapshot missing). Contact an administrator.'
  }
  if (payload.errorCode === 'PDF_UNAVAILABLE') {
    return 'PDF service temporarily unavailable. Try again later.'
  }
  if (payload.errorCode === 'ACCESS_DENIED') {
    return 'You cannot open this document.'
  }
  return 'Download failed'
}

async function parseBlobErrorMessage(blob: Blob): Promise<string> {
  try {
    const json = JSON.parse(await blob.text()) as {
      message?: ApiMessage | string
      fr?: string
      en?: string
      errorCode?: string
    }
    return pickApiMessage(json)
  } catch {
    return 'Download failed'
  }
}

async function classifyFileBlob(blob: Blob): Promise<{blob: Blob; extension: 'pdf' | 'html'}> {
  if (!blob || blob.size === 0) {
    throw new Error('Empty file')
  }
  const head = await blob.slice(0, 64).text()
  const trimmed = head.trimStart().toLowerCase()
  if (trimmed.startsWith('%pdf')) {
    const pdfBlob = blob.type.includes('pdf') ? blob : new Blob([blob], {type: 'application/pdf'})
    return {blob: pdfBlob, extension: 'pdf'}
  }
  if (trimmed.startsWith('{')) {
    throw new Error(await parseBlobErrorMessage(blob))
  }
  if (
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    trimmed.startsWith('<!') ||
    blob.type.includes('html')
  ) {
    const htmlBlob = blob.type.includes('html') ? blob : new Blob([blob], {type: 'text/html;charset=utf-8'})
    return {blob: htmlBlob, extension: 'html'}
  }
  const type = (blob.type || '').toLowerCase()
  if (type.includes('pdf') || type.includes('octet-stream')) {
    return {blob: new Blob([blob], {type: 'application/pdf'}), extension: 'pdf'}
  }
  throw new Error('The server did not return a PDF file')
}

/** Legacy static file URL (no JWT). */
export function legacyDocumentPdfUrl(pdfUrl?: string | null): string | null {
  if (!pdfUrl) return null
  return getFilePath(pdfUrl)
}

/**
 * Recommended: `GET /api/documents/:id/download` with JWT (see ADMIN-DOCUMENTS-FRONTEND-PDF-AND-FILES.md).
 */
export async function downloadOfficialDocument(documentId: string): Promise<Blob> {
  if (!getAuthToken()) {
    throw new Error('Not authenticated')
  }
  try {
    const res = await axios.get(documentDownloadUrl(documentId), {
      responseType: 'blob',
      headers: {Accept: PDF_ACCEPT},
      withCredentials: viewerRequestUsesCredentials(),
    })
    const {blob} = await classifyFileBlob(res.data as Blob)
    return blob
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
      throw new Error(await parseBlobErrorMessage(err.response.data))
    }
    throw err
  }
}

async function fetchLegacyPdfBlob(url: string): Promise<{blob: Blob; extension: 'pdf' | 'html'}> {
  const res = await fetch(url, {
    method: 'GET',
    headers: {Accept: PDF_ACCEPT},
    credentials: viewerRequestUsesCredentials() ? 'include' : 'omit',
    mode: 'cors',
  })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`)
  }
  return classifyFileBlob(await res.blob())
}

function saveBlobAsFile(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 200)
}

function logDocumentDownloadAudit(documentId: string): void {
  void fetchDocumentById(documentId, true).catch(() => {})
}

/** Open PDF in a new tab (secure download, legacy pdfUrl fallback). */
export async function openDocumentPdf(doc: GeneratedDocument): Promise<void> {
  const fileName = `${doc.documentNumber || 'document'}.pdf`

  if (doc._id) {
    try {
      const blob = await downloadOfficialDocument(doc._id)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      logDocumentDownloadAudit(doc._id)
      return
    } catch {
      /* fall through to legacy */
    }
  }

  const legacy = legacyDocumentPdfUrl(doc.pdfUrl)
  if (legacy) {
    const params = new URLSearchParams({file: legacy, fileName})
    window.open(`/file-viewer?${params.toString()}`, '_blank', 'noopener,noreferrer')
    return
  }

  throw new Error('No PDF available for this document')
}

/** Save PDF to disk (secure download, legacy pdfUrl fallback). */
export async function downloadDocumentPdf(doc: GeneratedDocument): Promise<DocumentDownloadResult> {
  const baseName = (doc.documentNumber || 'document').replace(/[^\w.-]+/g, '_')

  if (doc._id) {
    try {
      const blob = await downloadOfficialDocument(doc._id)
      const extension = blob.type.includes('html') ? 'html' : 'pdf'
      const fileName = `${baseName}.${extension}`
      saveBlobAsFile(blob, fileName)
      logDocumentDownloadAudit(doc._id)
      return {fileName, isHtmlFallback: extension === 'html'}
    } catch (primaryError) {
      const legacy = legacyDocumentPdfUrl(doc.pdfUrl)
      if (!legacy) {
        throw primaryError instanceof Error ? primaryError : new Error('Download failed')
      }
    }
  }

  const legacy = legacyDocumentPdfUrl(doc.pdfUrl)
  if (!legacy) {
    throw new Error('No PDF available')
  }
  const {blob, extension} = await fetchLegacyPdfBlob(legacy)
  const fileName = `${baseName}.${extension}`
  saveBlobAsFile(blob, fileName)
  return {fileName, isHtmlFallback: extension === 'html'}
}

export async function fetchDocumentById(id: string, download = false): Promise<GeneratedDocument> {
  const {data} = await axios.get(documentByIdUrl(id), {
    params: download ? {download: true} : undefined,
  })
  const doc = (data as {document?: GeneratedDocument}).document
  if (!doc) throw new Error('Document not found')
  return doc
}

export async function generateDocument(body: {
  templateId: string
  studentId?: string
  studentIds?: string[]
  schoolYear?: string
  extraContext?: Record<string, unknown>
}): Promise<GeneratedDocument | {generated: number; failed: number; results: unknown[]}> {
  const {data} = await axios.post(documentsGenerateUrl, body)
  const payload = data as {
    document?: GeneratedDocument
    generated?: number
    results?: unknown[]
  }
  if (payload.document) return payload.document
  return {
    generated: payload.generated ?? 0,
    failed: (data as {failed?: number}).failed ?? 0,
    results: payload.results ?? [],
  }
}

/** Generate then open PDF in a new tab (`GET /api/documents/:id/download`). */
export async function generateAndOpenDocument(body: {
  templateId: string
  studentId: string
  schoolYear?: string
  extraContext?: Record<string, unknown>
}): Promise<GeneratedDocument> {
  const result = await generateDocument(body)
  if (!('_id' in result) || !result._id) {
    throw new Error('Bulk or partial generate — open documents from the registry')
  }
  await openDocumentPdf(result)
  return result
}

export async function recordDocumentPrint(id: string, delivered?: boolean): Promise<GeneratedDocument> {
  const {data} = await axios.post(documentPrintUrl(id), delivered ? {delivered: true} : {})
  const doc = (data as {document?: GeneratedDocument}).document
  if (!doc) throw new Error('Invalid print response')
  return doc
}

export async function fetchDocumentHistory(id: string): Promise<{
  history: DocumentHistoryEntry[]
  printedCount: number
  delivered: boolean
}> {
  const {data} = await axios.get(documentHistoryUrl(id))
  const payload = data as {
    history?: DocumentHistoryEntry[]
    printedCount?: number
    delivered?: boolean
  }
  return {
    history: Array.isArray(payload.history) ? payload.history : [],
    printedCount: payload.printedCount ?? 0,
    delivered: Boolean(payload.delivered),
  }
}

export async function archiveDocument(id: string): Promise<GeneratedDocument> {
  const {data} = await axios.patch(documentArchiveUrl(id))
  const doc = (data as {document?: GeneratedDocument}).document
  if (!doc) throw new Error('Invalid archive response')
  return doc
}

/** Permanent delete (staff roles 2 / 6 / 10). Uses POST, not HTTP DELETE. */
export async function deleteDocument(id: string): Promise<void> {
  try {
    await axios.post(documentsDeleteUrl, {id, isDeleted: true})
  } catch (err) {
    if (isAxiosNotFound(err)) {
      await axios.post(documentDeleteByIdUrl(id), {isDeleted: true})
      return
    }
    throw err
  }
}

export async function verifyDocument(code: string): Promise<DocumentVerifyResult> {
  const {data} = await axios.get(documentVerifyUrl(code))
  return data as DocumentVerifyResult
}

/** Staff document workflows: admin (2), finance manager (6), secretary (10). */
export function isStaffRole(role: string | undefined): boolean {
  const idx = resolveRoleIndex(role)
  return idx === 2 || idx === 6 || idx === 10
}

export function isAxiosNotFound(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 404
}

export function isAxiosForbidden(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 403
}

export type {AxiosError}
